import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, Image, Text, UserIcon, FamilyIcon } from '@frontend/design-system';
import { useGetCompanyLogo, useGetLocationDetails } from '../../hooks/api-hooks';
import { useFillingExperienceContext } from '../filling-experience/provider';
import { CONTAINER_WIDTH, HEADER_HEIGHT_CSS_VAR } from './constants';
import { CountrySelector } from './country-selector.component';
import { useTrackElementHeightVar } from './use-track-element-height';

const headerStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.floating};
  width: 100%;
  z-index: 999;
`;

const contentContainerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.spacing(1)};
  max-width: ${CONTAINER_WIDTH}px;
  padding: ${theme.spacing(2, 1.5)};
  height: 100%;
  margin: 0 auto;
  min-height: 82px;
`;

const brandRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const patientRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing(1)};
`;

const patientNameStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const logoImageStyles = css`
  object-position: left center;
  width: auto !important;
`;

export const topRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

interface PatientInfo {
  firstName: string;
  lastName?: string;
  /** True when the solicited link was generated for a dependent of the patient. */
  isDependent: boolean;
}

interface Props {
  showCountrySelector?: boolean;
  /** Patient the solicited form was sent to; renders their name (and a Dependent chip) in the header. */
  patient?: PatientInfo;
  /** Optional locationId to use directly, bypassing the FillingExperienceContext.
   *  Use this when rendering Header outside of a FillingExperienceWrapper (e.g. error/fallback pages). */
  locationId?: string;
  /** When provided (including empty string), the logo is not fetched. */
  logoUrl?: string;
  /** When provided (including empty string), location details are not fetched. */
  officeName?: string;
}

type BrandingProps = Pick<Props, 'logoUrl' | 'officeName' | 'patient'>;

const HeaderContent = ({
  showCountrySelector,
  locationId,
  patient,
  logoUrl: logoUrlProp,
  officeName: officeNameProp,
}: Required<Pick<Props, 'showCountrySelector'>> &
  BrandingProps & {
    locationId: string;
  }) => {
  const { t } = useTranslation('digital-forms-experience');
  const { logoUrl: fetchedLogoUrl, isLoading: isLogoLoading } = useGetCompanyLogo(
    locationId,
    logoUrlProp === undefined
  );
  const logoUrl = logoUrlProp ?? fetchedLogoUrl;
  // Office name is only rendered as a fallback when there is no logo.
  const { locationDetails } = useGetLocationDetails(
    locationId,
    officeNameProp === undefined && !logoUrl && !isLogoLoading
  );
  const officeName = officeNameProp ?? (locationDetails?.location_nickname || locationDetails?.name);
  const headerRef = useTrackElementHeightVar(HEADER_HEIGHT_CSS_VAR);

  return (
    <header ref={headerRef} css={headerStyles}>
      <div css={contentContainerStyles}>
        <div css={brandRowStyles}>
          {logoUrl ? (
            <Image height={50} objectFit='contain' src={logoUrl} alt={t('Company Logo')} imageStyle={logoImageStyles} />
          ) : (
            officeName && <Text>{officeName}</Text>
          )}
          {showCountrySelector && <CountrySelector locationId={locationId} />}
        </div>
        {patient && (
          <div css={patientRowStyles}>
            <Text css={patientNameStyles}>
              {patient.isDependent ? <FamilyIcon size={18} /> : <UserIcon size={18} />}
              {[patient.firstName, patient.lastName].filter(Boolean).join(' ')}
            </Text>
            {patient.isDependent && <Chip variant='primary'>{t('Dependent')}</Chip>}
          </div>
        )}
      </div>
    </header>
  );
};

const HeaderWithContext = ({ showCountrySelector, ...branding }: { showCountrySelector: boolean } & BrandingProps) => {
  const { locationId } = useFillingExperienceContext();
  return <HeaderContent showCountrySelector={showCountrySelector} locationId={locationId} {...branding} />;
};

/**
 * Public entry-point for the page header.
 *
 * ## Why two inner components?
 *
 * `HeaderWithContext` calls `useFillingExperienceContext()`, which throws if it
 * is rendered outside of a `FillingExperienceWrapper` (i.e. outside the
 * provider). Most pages are wrapped in that provider and can rely on it to
 * supply the `locationId`, so calling the hook is safe.
 *
 * However, certain pages — such as error boundaries and standalone fallback
 * pages — are rendered *before* or *outside* the provider tree. For those
 * cases, callers pass `locationId` as a prop directly to `Header`. When that
 * prop is present, `Header` skips `HeaderWithContext` entirely and renders
 * `HeaderContent` directly, avoiding the hook call and any potential context
 * error.
 *
 * In short:
 * - **`locationId` prop provided** → renders `HeaderContent` directly (no context needed).
 * - **`locationId` prop omitted** → renders `HeaderWithContext`, which reads
 *   `locationId` from `FillingExperienceContext` (provider must be in the tree).
 */
const Header = ({ showCountrySelector = false, locationId, ...branding }: Props) => {
  if (locationId !== undefined) {
    return <HeaderContent showCountrySelector={showCountrySelector} locationId={locationId} {...branding} />;
  }
  return <HeaderWithContext showCountrySelector={showCountrySelector} {...branding} />;
};

export default Header;
